import { useI18n } from 'vue-i18n';

const isBlank = value => value === undefined || value === null || value === '';

const isValidDate = value => !Number.isNaN(new Date(value).getTime());

const isValidNumber = value => !Number.isNaN(Number(value));

const isValidListValue = (value, attributeValues) =>
  (attributeValues || [])
    .map(v => String(v).toLowerCase())
    .includes(String(value).toLowerCase());

const TYPE_VALIDATORS = {
  date: isValidDate,
  number: isValidNumber,
  currency: isValidNumber,
  percent: isValidNumber,
  list: (value, attributeValues) => isValidListValue(value, attributeValues),
  checkbox: (value, attributeValues) =>
    isValidListValue(value, attributeValues),
};

export function useCustomAttributeValueValidation() {
  const { t } = useI18n();

  /**
   * Checks the preview sample rows for a column mapped to a Custom Attribute
   * against its declared type, returning the first incompatible value found.
   * Best-effort: only the sample is checked, not the full file.
   */
  const findIncompatibleSampleValue = (previewRows, column, definition) => {
    const validator = TYPE_VALIDATORS[definition.attributeDisplayType];
    if (!validator) return null;

    const invalidRow = previewRows.find(row => {
      const value = row[column];
      if (isBlank(value)) return false;
      return !validator(value, definition.attributeValues);
    });

    return invalidRow ? invalidRow[column] : null;
  };

  const getFormatHint = definition => {
    switch (definition.attributeDisplayType) {
      case 'date':
        return t('CUSTOM_ATTRIBUTE_VALUE_VALIDATION.FORMAT_HINT.DATE');
      case 'number':
      case 'currency':
      case 'percent':
        return t('CUSTOM_ATTRIBUTE_VALUE_VALIDATION.FORMAT_HINT.NUMBER');
      case 'list':
      case 'checkbox':
        return t('CUSTOM_ATTRIBUTE_VALUE_VALIDATION.FORMAT_HINT.LIST', {
          options: (definition.attributeValues || []).join(', '),
        });
      default:
        return '';
    }
  };

  return { findIncompatibleSampleValue, getFormatHint };
}
